import React, { Suspense } from 'react';
import type { CBCTPatient } from '../components/cbct/CBCTViewer';
import CbctProcessingGate from '../components/cbct/CbctProcessingGate';
import type { ScanStorage } from '../components/cbct/scanStore';
import PDFViewer from '../components/PDFViewer';
import { VideoPlayer } from '../components/VideoPlayer';
import { hasCbctSources } from './fileTypes';
import { ImageCarousel } from './ImageCarousel';
import type { PreviewAction } from './previewActionMenuModel';
import type { Asset, CbctExportHandler, PreviewType } from './types';
import type { CbctProcessingPhase } from './useCbctProcessing';

// Lazy so the heavy three.js / vtk.js stacks stay out of the host's main chunk.
const STLViewer = React.lazy(() =>
    import('../components/stl/STLViewer').then((m) => ({ default: m.default }))
);

const CBCTViewer = React.lazy(() =>
    import('../components/cbct/CBCTViewer').then((m) => ({ default: m.default }))
);

interface PreviewContentProps {
    previewType: PreviewType | null;
    previewAssets: Asset[];

    /** Base URL of the host's API - used by the PDF/STL viewers' proxy fetches. */
    apiBaseUrl?: string;
    /** Bearer token supplier for the PDF/STL viewers' proxy fetches. */
    getAuthToken?: () => string | null;

    /** Backend-backed persistence for CBCT markings (arch/TMJ/measurements/annotations). */
    cbctStorage?: ScanStorage;
    onCbctExport?: CbctExportHandler;
    /** Patient name + scan date, shown in the CBCT viewer's top bar. */
    cbctPatient?: CBCTPatient;
    /** Open the CBCT viewer read-only (no marking edits). */
    cbctReadOnly?: boolean;
    /** Present only when the host supplies FilesApi.cbctProcessing: renders the
     *  processing gate instead of the viewer for CBCT scans without sources. */
    cbctProcessingGate?: {
        phase: CbctProcessingPhase;
        error?: string;
        onProcess: (password?: string) => void;
    };
    /** Per-image actions in the carousel toolbar's "More actions" menu. */
    imageActions?: PreviewAction[];
}

const PreviewContent: React.FC<PreviewContentProps> = ({
    previewType,
    previewAssets,
    apiBaseUrl = '',
    getAuthToken,
    cbctStorage,
    onCbctExport,
    cbctPatient,
    cbctReadOnly,
    cbctProcessingGate,
    imageActions
}) => {
    if (!previewType || previewAssets.length === 0) return null;

    switch (previewType) {
        case 'image':
            return (
                <div className="relative w-full h-full overflow-hidden bg-gray-50 rounded-lg">
                    <ImageCarousel
                        images={previewAssets.map((a) => a.publicUrl)}
                        className="w-full h-full"
                        actions={imageActions}
                    />
                </div>
            );
        case 'pdf':
            return (
                <div className="relative w-full h-full overflow-hidden">
                    <PDFViewer
                        pdfUrl={previewAssets[0].publicUrl}
                        apiBaseUrl={apiBaseUrl}
                        showControls={true}
                        className="h-full"
                        getAuthToken={getAuthToken}
                    />
                </div>
            );
        case 'video':
            return (
                <div className="relative w-full h-full overflow-hidden bg-black rounded-lg">
                    <VideoPlayer videoUrl={previewAssets[0].publicUrl} className="w-full h-full" />
                </div>
            );
        case 'stl':
            return (
                <div className="relative w-full h-full">
                    <Suspense
                        fallback={
                            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-xl">
                                <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                            </div>
                        }
                    >
                        <STLViewer
                            stlUrl={previewAssets[0].publicUrl}
                            apiBaseUrl={apiBaseUrl}
                            className="h-full"
                        />
                    </Suspense>
                </div>
            );
        case 'cbct': {
            const manifest = previewAssets[0];
            const volume = previewAssets[1];
            if (!manifest) return null;
            // Single owner of the gate rule: capability present + no processed
            // refs. Once refresh lands manifest/binary this falls through to
            // the viewer (in-place swap).
            if (cbctProcessingGate && !hasCbctSources(manifest)) {
                return (
                    <CbctProcessingGate
                        fileName={manifest.name}
                        phase={cbctProcessingGate.phase}
                        error={cbctProcessingGate.error}
                        onProcess={cbctProcessingGate.onProcess}
                    />
                );
            }
            // Signed viewer-source URLs still resolving in the host.
            if (!manifest.publicUrl) {
                return (
                    <div className="flex items-center justify-center w-full h-full bg-gray-50 rounded-lg">
                        <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                    </div>
                );
            }
            // cbctStorage is only unset if the host forgot to wire it up - every
            // real CBCT asset is backend-persisted, keyed by its patientFileId.
            if (!cbctStorage) return null;

            const scanId = String(manifest.patientFileId ?? manifest.assetId);
            return (
                <div className="absolute inset-0 z-10">
                    <Suspense
                        fallback={
                            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-xl">
                                <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                            </div>
                        }
                    >
                        <CBCTViewer
                            manifestUrl={manifest.publicUrl}
                            volumeUrl={volume?.publicUrl}
                            scanId={scanId}
                            storage={cbctStorage}
                            className="h-full"
                            onExport={onCbctExport}
                            patient={cbctPatient}
                            readOnly={cbctReadOnly}
                        />
                    </Suspense>
                </div>
            );
        }
        default:
            return null;
    }
};

export { PreviewContent };
